import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", payment_status: null, attempts: 0 });
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;
    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2500;

    const poll = async () => {
      if (stopRef.current) return;
      if (!sessionId) { setState({ status: "error", payment_status: "missing_session" }); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        attempts += 1;
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", payment_status: "paid" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ status: "timeout", payment_status: data.payment_status });
          return;
        }
        setState({ status: "polling", payment_status: data.payment_status, attempts });
        setTimeout(poll, interval);
      } catch (e) {
        setState({ status: "error", payment_status: e?.response?.data?.detail || "unknown" });
      }
    };
    poll();
    return () => { stopRef.current = true; };
  }, [sessionId, refresh]);

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-blue-50 via-fuchsia-50 to-amber-50 flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-xl w-full px-6 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-zinc-400 mx-auto mb-6" />
            <div className="overline mb-4">PROCESSING</div>
            <h1 className="font-display font-extrabold text-4xl mb-3">Confirming your payment...</h1>
            <p className="text-zinc-500">This usually takes 5-10 seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="inline-flex w-16 h-16 bg-[#EF4444] items-center justify-center mb-6">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div className="overline mb-4 text-[#EF4444]">RESUMELAB PRO ACTIVATED</div>
            <h1 className="font-display font-extrabold text-5xl mb-4">You're in.</h1>
            <p className="text-zinc-500 mb-10">Unlimited audits, AI rewrites, cover letters and PDF export — all unlocked. Your subscription renews monthly until you cancel.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/evaluate" data-testid="post-pay-evaluate" className="btn-sharp bg-[#EF4444] hover:bg-[#002FA7] text-white">
                Run an audit <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/history" className="btn-sharp border border-zinc-300 hover:bg-zinc-900 hover:text-white">
                View history
              </Link>
            </div>
          </>
        )}
        {(state.status === "timeout" || state.status === "expired" || state.status === "error") && (
          <>
            <XCircle className="w-12 h-12 text-[#FF2A00] mx-auto mb-6" />
            <div className="overline mb-4">UNABLE TO CONFIRM</div>
            <h1 className="font-display font-extrabold text-3xl mb-3">
              {state.status === "expired" ? "Session expired" : "Something went wrong"}
            </h1>
            <p className="text-zinc-500 mb-8">
              We couldn't confirm your payment ({state.payment_status || "unknown"}). If you were charged, refresh in a minute — Pro will activate automatically.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/pricing" className="btn-sharp bg-[#EF4444] hover:bg-[#002FA7] text-white">Try again</Link>
              <Link to="/" className="btn-sharp border border-zinc-300 hover:bg-zinc-900 hover:text-white">Home</Link>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
